import { z } from 'zod'
import {
  ResolvedOperand,
  maybeString,
  maybeStringArray,
} from '@omnea-app/packages/universal/conditionality/evaluation/evaluation'
import { ExtractedAttributeSchema } from '@omnea-app/packages/universal/documents/extracted-attribute'
import { AttachedFileSchema } from '@omnea-app/packages/universal/form/elements/attachedFile'
import { BaseFormAnswerSchema } from '@omnea-app/packages/universal/formBuilder/BaseFormQuestionAndAnswerSchemas'
import { RequestFormCustomFieldPropertySchema } from '@omnea-app/packages/universal/formBuilder/customFieldTypes'
import {
  BaseQuestionConfigSchema,
  BaseQuestionDefinitionSchema,
  BaseQuestionInstanceSchema,
} from '@omnea-app/packages/universal/formBuilder/question'
import { ref } from '@omnea-app/packages/universal/references/refKinds'
import type { ValueOf } from '@omnea-digital/toolbox'

export const FileUploadType = {
  ZIP: 'zip',
  IMAGE: 'image',
  /** @deprecated - we've removed the ability to add this option when configuring supported types
   * but we still  need to support it here for existing config.*/
  ALL_DOC_TYPES: 'document', // doc, docx, xls, xlsx, csv
  PDF: 'pdf',
  CSV: 'csv',
  WORD: 'word',
  EXCEL: 'excel',
} as const
export type FileUploadType = ValueOf<typeof FileUploadType>

export const UploadableFileType = FileUploadType
export type UploadableFileType = FileUploadType

export const ALL_FILE_UPLOAD_TYPES = [
  FileUploadType.ZIP,
  FileUploadType.IMAGE,
  FileUploadType.PDF,
  FileUploadType.CSV,
  FileUploadType.WORD,
  FileUploadType.EXCEL,
]

export const FilesQuestionDefinitionSchema = z.object({
  ...BaseQuestionDefinitionSchema.shape,
  type: z.literal('files'),
  storeFilesGlobally: z.boolean().optional(),
  documentExtractionEnabled: z.boolean().optional(),
  documentTypeId: ref('documentType').optional(),
  customField: RequestFormCustomFieldPropertySchema.optional(),
  supportedTypes: z.array(z.enum(FileUploadType)),
  multipleFiles: z.boolean(),
  versioned: z.boolean().optional(),
  allowAllDocumentTypes: z.boolean().optional(),
  templateFiles: z.array(AttachedFileSchema).optional(),
})
export type FilesQuestionDefinition = z.infer<typeof FilesQuestionDefinitionSchema>

export const FilesQuestionInstanceSchema = z.object({ ...BaseQuestionInstanceSchema.shape, type: z.literal('files') })
export type FilesQuestionInstance = z.infer<typeof FilesQuestionInstanceSchema>

export const FilesQuestionConfigSchema = z
  .object({ ...FilesQuestionDefinitionSchema.shape, ...BaseQuestionConfigSchema.shape })
  .extend({})
export type FilesQuestionConfig = z.infer<typeof FilesQuestionConfigSchema>

export const FilesFormQuestionSchema = z.object({ ...FilesQuestionInstanceSchema.shape, ...FilesQuestionConfigSchema.shape })
export type FilesFormQuestion = z.infer<typeof FilesFormQuestionSchema>

export const REVIEW_OUTCOMES = ['high', 'medium', 'pass'] as const
export type ReviewOutcome = (typeof REVIEW_OUTCOMES)[number]

export const outcomeSchema = z.enum(REVIEW_OUTCOMES)

/** Ranks a set of values by a severity map, for the several axes document review is graded on. */
export const highestSeverityBy =
  <T extends string>(severity: Record<T, number>) =>
  (values: T[]): T | null =>
    values.reduce<T | null>((highest, value) => (highest === null || severity[value] > severity[highest] ? value : highest), null)

const OUTCOME_SEVERITY: Record<ReviewOutcome, number> = {
  pass: 0,
  medium: 1,
  high: 2,
}

/** Callers decide what a missing review means before calling — this ranks known outcomes only. */
export const highestSeverityOutcome = highestSeverityBy(OUTCOME_SEVERITY)

export const REVIEW_STATUSES = ['success', 'partial'] as const
export type ReviewStatus = (typeof REVIEW_STATUSES)[number]

export const reviewStatusSchema = z.enum(REVIEW_STATUSES)

// A review that ran, not something the form declares. Answer-side ids sit beside the question
// definition in this file and none of them belong in a design-time reference index.
export const DocumentReviewResultSchema = z.object({
  documentReviewId: z.uuid(),
  outcome: outcomeSchema,
  status: reviewStatusSchema,
  playbookId: z.uuid(),
  reviewedAt: z.iso.datetime({ offset: true }),
})
export type DocumentReviewResult = z.infer<typeof DocumentReviewResultSchema>

/** One file can answer several questions, each reviewing it against its own playbook, so a review belongs to the pair. */
export const documentReviewKey = (fileStoreId: string, documentTypeId: string): string =>
  JSON.stringify([fileStoreId, documentTypeId])

/** Display-only: what makes a file staged is its key differing from the document's head. */
const StagedReplacementSchema = z.object({
  replacedFilestoreId: z.string(),
  stagedByUserId: z.uuid(),
  stagedAt: z.iso.datetime({ offset: true }),
})

export const UploadedFileItemSchema = z.object({
  filename: z.string(),
  key: z.string(),
  extractedAttributes: z.array(ExtractedAttributeSchema).optional(),
  documentReviewResult: DocumentReviewResultSchema.optional(),
  stagedReplacement: StagedReplacementSchema.optional(),
})

export type UploadedFileItem = z.infer<typeof UploadedFileItemSchema>

export const FilesFormAnswerSchema = z.object({
  ...BaseFormAnswerSchema.shape,
  type: z.literal('files'),
  value: z.array(UploadedFileItemSchema),
  storeFilesGlobally: z.boolean().optional(),
  customField: RequestFormCustomFieldPropertySchema.optional(),
})
export type FilesFormAnswer = z.infer<typeof FilesFormAnswerSchema>

export enum FileConditionVariants {
  DOCUMENT_REVIEW_OUTCOME = 'DOCUMENT_REVIEW_OUTCOME',
}

// A file whose review is missing counts as 'high': a customer condition must not silently pass.
const resolveFileOutcome = (file: Pick<UploadedFileItem, 'documentReviewResult'>): ReviewOutcome =>
  file.documentReviewResult?.outcome ?? 'high'

export const getDocumentReviewOutcomeConditionValue = (
  files: Pick<UploadedFileItem, 'documentReviewResult'>[],
  variant: string | null
): ResolvedOperand<'string'> | null => {
  if (variant !== FileConditionVariants.DOCUMENT_REVIEW_OUTCOME) return null
  if (files.length === 0) return null

  // If no files have a review result, the question likely has no playbook configured.
  // Return null (not evaluable) rather than a false-positive 'high'.
  if (!files.some((f) => f.documentReviewResult != null)) return null

  const highest = highestSeverityOutcome(files.map(resolveFileOutcome))

  return highest === null ? null : maybeString(highest)
}

export const getFilesUploadedConditionValue = (files: Pick<UploadedFileItem, 'key'>[]): ResolvedOperand<'stringArray'> | null => {
  return maybeStringArray(files.map((f) => f.key))
}
